import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent, CircularProgress,
  Fab,
  Snackbar,
  Stack,
  Typography
} from '@mui/material'
import { OrgAddressCard } from 'components'
import React, { useEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import {
  getOrgAddressesList,
  type OrganizationAddress,
  patchOrgAddress
} from 'services'

export interface OrganizationAddressProps {
  orgId: number
}

export const OrganizationAddressesBlock: React.FC<OrganizationAddressProps> = ({ orgId }) => {
  const [expanded, setExpanded] = useState(true)
  const [addresses, setAddresses] = useState<OrganizationAddress[]>([])
  const [loading, setLoading] = useState(true)
  const [reloadAddresses, setReloadAddresses] = useState(true)
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        const addressesResult = await getOrgAddressesList(orgId)
        setAddresses(addressesResult)
      } catch (error) {
        setAddresses([])
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
  }, [orgId, reloadAddresses])

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const moveCard = (dragIndex: number, hoverIndex: number): void => {
    const newAddresses = [...addresses]
    const draggedCard = newAddresses[dragIndex]

    newAddresses.splice(dragIndex, 1)
    newAddresses.splice(hoverIndex, 0, draggedCard)
    const requestData = JSON.stringify({
      [newAddresses[dragIndex].id]: dragIndex + 1,
      [newAddresses[hoverIndex].id]: hoverIndex + 1
    })

    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true)
        await patchOrgAddress(orgId, draggedCard.id, requestData)
      } catch (error) {
        setAddresses([])
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    void fetchData()
    setAddresses(newAddresses)
  }

  const addAddress = (): void => {
    const newAddresses = [...addresses]
    const newAddress: OrganizationAddress = {
      id: 0,
      organizationId: orgId,
      primaryAddress: {
        addressOne: '',
        addressTwo: '',
        city: '',
        state: null,
        postalCode: '',
        mailingAddress: true,
        id: undefined,
        country: null
      },
      secondaryAddress: {
        addressOne: '',
        addressTwo: '',
        city: '',
        state: null,
        postalCode: '',
        mailingAddress: false,
        id: undefined,
        country: null
      },
      phone: {
        id: 0,
        organizationId: orgId,
        addressId: 0,
        countryCode: '',
        phoneNumber: '',
        extension: '',
        displayOrder: 0
      },
      displayOrder: null
    }
    newAddresses.push(newAddress)
    setAddresses(newAddresses)
  }
  const handleDelete = (index: number): void => {
    const newAddresses = [...addresses]
    newAddresses.splice(index, 1)
    setAddresses(newAddresses)
    setShowSuccessAlert(true)
  }

  function handleSnackbarClose (_event: React.SyntheticEvent | Event, reason?: string): void {
    if (reason === 'clickaway') {
      return
    }

    setShowSuccessAlert(false)
  }

  return (
        <Card sx={{ borderRadius: '12px', width: '100%' }}>
            <Accordion expanded={expanded} onChange={handleAccordionChange}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography variant="h6">Addresses</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <CardContent>
                      {loading && <Box display={'flex'} justifyContent={'center'}> <CircularProgress/></Box>}
                      {!loading &&
                      <Stack spacing={2}>
                            <DndProvider backend={HTML5Backend}>
                            {addresses?.map?.((value1, index) => (
                                    <OrgAddressCard deleteAddress={handleDelete} key={value1.id} address={value1} index={index} moveCard={moveCard} reloadAddresses={() => { setReloadAddresses(prevState => !prevState) }}/>
                            ))}
                              <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                                <Fab color="primary" aria-label="add" onClick={() => { addAddress() }}>
                                    <AddIcon/>
                                </Fab>
                              </Box>
                            </DndProvider>
                        </Stack>}
                    </CardContent>
                    <Card>
                      {showSuccessAlert && (
                        <Snackbar open={showSuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
                          <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                            The Address was deleted Successfully !
                          </Alert>
                        </Snackbar>
                      )}
                  </Card>
                </AccordionDetails>
            </Accordion>
        </Card>
  )
}
